import { cloneDeep, isNil } from 'lodash';
import { useEffect, useState } from 'react';

import { LessonComponentBase } from '../../content/models/LessonComponent';
import { UpsertAnswerData } from '../../dashboard/gql-types';
import { useAnswers } from '../../dashboard/hooks/useAnswers';
import { useLessonComponents } from './useLessonComponents';

export function useLessonComponentsWithAnswers(
  lessonkey: number,
  lessonVersion: number,
  userkey: number,
  layer: number,
  withAnswers: boolean = true
) {
  const [lessonComponents, setLessonComponents] = useState<
    LessonComponentBase[]
  >([]);

  const {
    loading,
    error: loadError,
    lessonComponents: _lessonComponents
  } = useLessonComponents(lessonkey, lessonVersion);

  const {
    answers,
    upsertAnswer,
    savingAnswer,
    saveAnswerError,
    savedAnswer: savedAnswerData,
    deleteAnswer,
    deletingAnswer,
    deleteAnswerError,
    deletedAnswer
  } = useAnswers(userkey, lessonkey, layer, withAnswers && !!_lessonComponents);

  useEffect(() => {
    if (!(_lessonComponents && (!withAnswers || answers))) {
      return;
    }

    const copy = cloneDeep(_lessonComponents);
    copy.forEach((component) => {
      const answerObject = answers?.find(
        (a) => a.componentkey === component.componentkey
      );
      component.answer = answerObject?.answer ?? null;
      component.answerModified = answerObject?.modified ?? null;
      component.mark = isNil(answerObject?.mark) ? null : answerObject.mark;
    });

    setLessonComponents(copy);
  }, [_lessonComponents, answers]);

  useEffect(() => {
    if (deletedAnswer) {
      setLessonComponents((value) => {
        const copy = cloneDeep(value);
        const deletedComponent = copy.find(
          (c) => c.componentkey === deletedAnswer
        );
        deletedComponent.answer = null;
        return copy;
      });
    }
  }, [deletedAnswer]);

  const saveAnswer = async (
    componentkey: number,
    answer: string,
    points: number
  ) => {
    const savedAnswerData = await upsertAnswer(componentkey, answer, points);
    if (savedAnswerData?.data?.upsertAnswer) {
      const savedAnswer = savedAnswerData?.data?.upsertAnswer;
      setLessonComponents((value) => {
        const copy = cloneDeep(value);
        const savedComponent = copy.find(
          (c) => c.componentkey === savedAnswer.componentkey
        );
        savedComponent.answer = savedAnswer.answer;
        savedComponent.answerModified = savedAnswer.modified;
        savedComponent.mark = savedAnswer.mark;
        return copy;
      });
    }
  };

  function updateMark(componentkey: number, mark: number) {
    setLessonComponents((components) => {
      const copy = cloneDeep(components);
      const component = copy.find((c) => c.componentkey === componentkey);
      component.mark = mark;
      return copy;
    });
  }

  return {
    loading,
    loadError,
    lessonComponents,
    upsertAnswer: saveAnswer,
    savingAnswer,
    saveAnswerError,
    updateMark,
    deleteAnswer,
    deletingAnswer,
    deleteAnswerError,
    deletedAnswer
  };
}
